import { type FC, useMemo } from 'react';

import {
  useCreateClientEvent,
  useCurrentUser,
  useGetClientEvents,
} from '@/hooks';
import { CardType } from '@/types';
import { Avatar, Button, Icon, Typography } from '@/ui';

import { DISMISS_FOREVER_TILL, isDismissed } from './bannerDismissal';

import memberOverviewImage from '@/components/Moments/member-overview-no-coach.png';

/** Custom Moments insights icon from Figma */
export const InsightsIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    height="12"
    viewBox="0 0 12 12"
    width="12"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      height="12"
      id="mask0_insights"
      maskUnits="userSpaceOnUse"
      style={{ maskType: 'alpha' }}
      width="12"
      x="0"
      y="0"
    >
      <rect fill="#D9D9D9" height="12" width="12" />
    </mask>
    <g mask="url(#mask0_insights)">
      <path
        d="M1.49891 9.60078C1.25068 9.60078 1.03906 9.5124 0.864063 9.33563C0.689062 9.15886 0.601562 8.94636 0.601562 8.69813C0.601562 8.4499 0.689229 8.23828 0.864563 8.06328C1.0399 7.88828 1.25069 7.80078 1.49691 7.80078C1.54168 7.80078 1.58281 7.80287 1.62031 7.80703C1.65781 7.8112 1.6974 7.81745 1.73906 7.82578L4.22656 5.33828C4.21823 5.29661 4.21198 5.25703 4.20781 5.21953C4.20365 5.18203 4.20156 5.1409 4.20156 5.09613C4.20156 4.84991 4.28995 4.63913 4.46671 4.46379C4.64349 4.28845 4.85599 4.20078 5.10421 4.20078C5.35245 4.20078 5.56406 4.28891 5.73906 4.46516C5.91406 4.64141 6.00156 4.85328 6.00156 5.10078C6.00156 5.14245 5.99323 5.22161 5.97656 5.33828L7.56406 6.92578C7.60573 6.91745 7.64531 6.9112 7.68281 6.90703C7.72031 6.90286 7.7599 6.90078 7.80156 6.90078C7.84323 6.90078 7.88281 6.90286 7.92031 6.90703C7.95781 6.9112 7.9974 6.91745 8.03906 6.92578L9.62656 5.33828C9.61823 5.29661 9.61198 5.25703 9.60781 5.21953C9.60365 5.18203 9.60156 5.1409 9.60156 5.09613C9.60156 4.84991 9.68995 4.63913 9.86671 4.46379C10.0435 4.28845 10.256 4.20078 10.5042 4.20078C10.7524 4.20078 10.9641 4.28916 11.1391 4.46593C11.3141 4.64271 11.4016 4.85521 11.4016 5.10343C11.4016 5.35167 11.3139 5.56328 11.1385 5.73828C10.9632 5.91328 10.7524 6.00078 10.5062 6.00078C10.4614 6.00078 10.4203 5.9987 10.3828 5.99453C10.3453 5.99037 10.3057 5.98411 10.2641 5.97578L8.67656 7.56328C8.6849 7.60495 8.69115 7.64453 8.69531 7.68203C8.69948 7.71953 8.70156 7.76066 8.70156 7.80543C8.70156 8.05166 8.61318 8.26244 8.43641 8.43777C8.25964 8.61311 8.04714 8.70078 7.79891 8.70078C7.55068 8.70078 7.33906 8.61311 7.16406 8.43777C6.98906 8.26244 6.90156 8.05166 6.90156 7.80543C6.90156 7.76066 6.90365 7.71953 6.90781 7.68203C6.91198 7.64453 6.91823 7.60495 6.92656 7.56328L5.33906 5.97578C5.2974 5.98411 5.25781 5.99037 5.22031 5.99453C5.18281 5.9987 5.14323 6.00078 5.10156 6.00078C5.0599 6.00078 4.98073 5.99245 4.86406 5.97578L2.37656 8.46328C2.3849 8.50495 2.39115 8.54453 2.39531 8.58203C2.39948 8.61953 2.40156 8.66067 2.40156 8.70543C2.40156 8.95166 2.31318 9.16244 2.13641 9.33777C1.95964 9.51311 1.74714 9.60078 1.49891 9.60078ZM2.10156 4.80078L1.81675 4.18559L1.20156 3.90078L1.81675 3.61597L2.10156 3.00078L2.38638 3.61597L3.00156 3.90078L2.38638 4.18559L2.10156 4.80078ZM7.50156 4.80078L7.03906 3.76328L6.00156 3.30078L7.03906 2.83828L7.50156 1.80078L7.96406 2.83828L9.00156 3.30078L7.96406 3.76328L7.50156 4.80078Z"
        fill="currentColor"
      />
    </g>
  </svg>
);

export interface MomentsPromoBannerPureProps {
  /** Coach's first name */
  firstName: string;
  /** Coach's last name */
  lastName: string;
  /** Coach's full name (optional, will default to firstName + lastName) */
  fullName?: string;
  /** Coach's avatar URL */
  avatarUrl?: string;
  /** URL to link to Moments */
  momentsUrl: string;
  /** Callback when dismiss button is clicked */
  onDismiss?: () => void;
}

/**
 * Presentational component for the Moments promotional banner.
 * Use this for Storybook or when you need to pass props directly.
 */
export const MomentsPromoBannerPure: FC<MomentsPromoBannerPureProps> = ({
  firstName,
  lastName,
  fullName,
  avatarUrl,
  momentsUrl,
  onDismiss,
}) => (
  <div className="relative mb-6 flex flex-col overflow-hidden rounded-xl bg-sage-500 shadow-lg md:flex-row md:items-stretch">
    {/* Decorative blob shapes */}
    {/* NOTE: These are exported from Figma as large rotated SVGs.
        The key is to rotate INSIDE a large positioning wrapper so the rotation pivot matches Figma,
        and to use a viewBox that actually contains the (very large) path coordinates. */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute flex items-center justify-center"
      style={{
        // Match Figma layout for the banner background waves.
        // Nudge so the diagonal break starts a bit further right (closer to Figma crop).
        height: '2214.07px',
        left: '-900px',
        top: '-620px',
        width: '2247.959px',
      }}
    >
      <div style={{ transform: 'rotate(306.851deg)' }}>
        <svg
          fill="none"
          style={{ height: '1678.186px', width: '1509.128px' }}
          // Path bounds (from source `d`): x [-683.252..991.212], y [-232.399..1520.47]
          // Add a bit of padding to avoid edge clipping after rotation.
          viewBox="-720 -270 1750 1820"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M-542.051 331.044C-597.463 195.067 -487.827 48.7846 -341.764 63.8109L-189.567 79.4681C-122.722 86.3449 -56.9981 58.1735 -15.7726 4.97378L78.0909 -116.153C168.172 -232.399 349.405 -210.793 409.174 -76.6837L471.452 63.0572C498.805 124.432 555.934 167.249 622.519 176.279L774.121 196.839C919.615 216.57 991.212 384.458 904.917 503.541L814.999 627.625C775.506 682.123 766.911 753.112 792.271 815.341L850.01 957.028C905.423 1093 795.786 1239.29 649.723 1224.26L497.527 1208.6C430.681 1201.73 364.958 1229.9 323.732 1283.1L229.869 1404.22C139.788 1520.47 -41.4458 1498.86 -101.214 1364.75L-163.492 1225.01C-190.845 1163.64 -247.974 1120.82 -314.559 1111.79L-466.162 1091.23C-611.655 1071.5 -683.252 903.613 -596.958 784.53L-507.04 660.446C-467.547 605.948 -458.952 534.96 -484.311 472.73L-542.051 331.044Z"
            fill="#505A54"
          />
        </svg>
      </div>
    </div>

    <div
      aria-hidden="true"
      className="pointer-events-none absolute flex items-center justify-center"
      style={{
        // Match Figma layout for the banner background waves.
        // Nudge up/left so the right curve frames the card more like Figma.
        height: '1719.413px',
        left: '280px',
        top: '-1200px',
        width: '1777.592px',
      }}
    >
      <div style={{ transform: 'rotate(297.594deg)' }}>
        <svg
          fill="none"
          style={{ height: '1364.471px', width: '1226.951px' }}
          // Path bounds (from source `d`): x [-38.8602..1284.61], y [-1102.8..320.215]
          // Add a bit of padding to avoid edge clipping after rotation.
          viewBox="-80 -1140 1400 1480"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M23.3934 -551.43C-38.8602 -653.297 29.9835 -785.017 149.159 -792.06L273.339 -799.399C327.88 -802.623 376.935 -833.823 403.058 -881.904L462.535 -991.376C519.615 -1096.44 667.875 -1102.8 733.38 -1003L801.635 -899.011C831.613 -853.337 883.058 -826.45 937.671 -827.912L1062.02 -831.239C1181.35 -834.432 1260.77 -709.075 1207.09 -602.232L1151.17 -490.903C1126.61 -442.007 1129 -383.919 1157.49 -337.3L1222.35 -231.155C1284.61 -129.288 1215.76 2.43274 1096.59 9.47588L972.409 16.8148C917.868 20.0381 868.812 51.2387 842.69 99.3196L783.213 208.792C726.132 313.852 577.873 320.215 512.368 220.416L444.113 116.426C414.134 70.7528 362.69 43.8655 308.077 45.3269L183.732 48.6543C64.3981 51.8476 -15.0175 -73.5098 38.6533 -180.352L94.5778 -291.681C119.14 -340.578 116.751 -398.666 88.2611 -445.285L23.3934 -551.43Z"
            fill="#505A54"
          />
        </svg>
      </div>
    </div>

    {/* Left content section - 5/12 width */}
    <div className="relative z-10 flex flex-col justify-center p-6 md:w-5/12 md:pl-14 md:pr-6">
      {/* Tag */}
      <div className="mb-4 flex items-center gap-2">
        <InsightsIcon className="text-white" />
        <Typography className="text-xs font-medium text-white" variant="body2">
          New Tool for Coaches
        </Typography>
      </div>

      {/* Headline */}
      <Typography
        className="mb-4 font-bold leading-tight text-white"
        variant="h4"
      >
        Better Coaching With Less Admin Work
      </Typography>

      {/* Description */}
      <Typography className="mb-6 text-white/90" variant="body1">
        Improve your craft with insights, summaries, feedback and more generated
        from your session transcriptions.
      </Typography>

      {/* CTA Button */}
      <a className="block" href={momentsUrl} rel="noreferrer" target="_blank">
        <Button
          className="w-full bg-sunny-500 text-cement-600 hover:bg-sunny-600"
          size="large"
        >
          Try Moments Now
        </Button>
      </a>
    </div>

    {/* Right preview card section - 7/12 width */}
    <div className="relative z-10 hidden md:flex md:w-7/12 md:items-center md:justify-center md:pl-4 md:pr-14 md:pt-6">
      <div className="relative w-full overflow-hidden rounded-t-xl bg-sage-200 shadow-xl">
        {/* Coach info header */}
        <div className="flex items-center justify-between gap-4 border-b border-cement-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <Avatar
              firstName={firstName}
              lastName={lastName}
              size="xs"
              src={avatarUrl ?? ''}
            />
            <div className="flex flex-col">
              <Typography
                className="font-bold leading-tight text-cement-500"
                variant="caption"
              >
                {fullName || `${firstName} ${lastName}`}
              </Typography>
              <span className="text-2xs text-cement-400">Coach</span>
            </div>
          </div>

          {/* Gradient button preview (non-interactive) */}
          <div
            className="pointer-events-none flex cursor-default select-none items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold text-white"
            style={{
              backgroundImage:
                'linear-gradient(160deg, rgb(255, 132, 98) 0%, rgb(221, 162, 217) 98%)',
            }}
          >
            <InsightsIcon className="text-white" />
            <span className="text-3xs">Give Me Coaching Feedback</span>
          </div>
        </div>

        {/* Member overview image */}
        <div className="relative h-64 overflow-hidden">
          <img
            alt="Moments member overview preview"
            className="absolute left-0 top-0 w-full object-cover object-top"
            src={memberOverviewImage}
          />
          {/* Fade out at bottom */}
          <div className="absolute bottom-0 left-0 h-16 w-full bg-gradient-to-t from-sage-200 to-transparent" />
        </div>
      </div>
    </div>

    {/* Close button */}
    <button
      aria-label="Dismiss banner"
      className="absolute right-4 top-4 z-20 border-none text-white transition-all hover:font-bold"
      style={{ background: 'none', cursor: 'pointer' }}
      type="button"
      onClick={onDismiss}
    >
      <Icon>close</Icon>
    </button>
  </div>
);

/**
 * Large promotional banner for Moments AI tool, displayed on the coach dashboard.
 * Features coach info with the member overview preview image.
 * Dismissable via client events.
 *
 * This is a container component that uses hooks.
 * For Storybook or testing, use MomentsPromoBannerPure instead.
 */
const MomentsPromoBanner: FC = () => {
  const { user } = useCurrentUser();
  const { data: clientEvents } = useGetClientEvents();
  const createClientEvent = useCreateClientEvent();

  const { momentsBaseUrl } = user;
  const enabled = !!momentsBaseUrl;

  const dismissed = useMemo(() => {
    const row = (clientEvents || []).find(
      (e) => e.key === CardType.COACH_MOMENTS_BANNER,
    );
    return isDismissed(row?.dismissedTill);
  }, [clientEvents]);

  // Avoid a "flash" of the banner before we know whether it's been dismissed.
  if (!enabled || clientEvents === undefined || dismissed) return null;

  const handleDismiss = () => {
    createClientEvent.mutate({
      dismissedTill: DISMISS_FOREVER_TILL,
      key: CardType.COACH_MOMENTS_BANNER,
    });
  };

  return (
    <MomentsPromoBannerPure
      avatarUrl={user.avatarUrl ?? undefined}
      firstName={user.firstName}
      fullName={user.fullName}
      lastName={user.lastName}
      momentsUrl={momentsBaseUrl!}
      onDismiss={handleDismiss}
    />
  );
};

export default MomentsPromoBanner;
